'use client';

import { useMemo, useState } from 'react';
import type { PublicShareManifest, SnapshotItem } from '@containd/shared';
import { resolveShareMediaUrl } from '@/lib/shareMediaUrl';
import { resolveIngestThumbUrl } from '@/lib/shareCanvasThumb';
import { PhotoLightbox } from './PhotoLightbox';
import { PinterestPinButton } from './SocialShareBar';
import { absolutizeUrl } from '@containd/shared';
import styles from './ItemViewer.module.css';
import { resolveShareLightboxUrl } from '@/lib/animatedSharePath';

type Tab = 'overview' | 'photos';

function mediaById(manifest: PublicShareManifest, mediaId: string | undefined) {
  if (!mediaId) return undefined;
  return manifest.media.find((x) => x.id === mediaId);
}

function mediaUrl(
  manifest: PublicShareManifest,
  mediaId: string | undefined,
): string | null {
  return resolveShareMediaUrl(manifest, mediaId);
}

/** Prefer listed `_thumb` sibling for grid cells; fall back to full. */
function thumbUrlForMedia(
  manifest: PublicShareManifest,
  mediaId: string,
): string {
  const m = mediaById(manifest, mediaId);
  if (!m) return '';
  return (
    resolveIngestThumbUrl(manifest, m) ??
    resolveShareMediaUrl(manifest, m) ??
    ''
  );
}

function fullUrlForMedia(
  manifest: PublicShareManifest,
  mediaId: string,
): string | null {
  return mediaUrl(manifest, mediaId);
}

function resolveLocalized(
  value: unknown,
  locale: string,
): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const map = value as Record<string, string>;
    return map[locale] ?? map.en ?? Object.values(map)[0] ?? '';
  }
  return value == null ? '' : String(value);
}

function resolveItemField(item: SnapshotItem, field: string): unknown {
  const anyItem = item as unknown as Record<string, unknown>;
  if (field in anyItem) return anyItem[field];
  if (field === 'propertyText') return item.propertyText;
  return undefined;
}

function resolveSlotValue(
  source: { kind?: string; value?: unknown; ref?: { kind?: string; field?: string } },
  item: SnapshotItem,
  locale: string,
): string | null {
  if (!source) return null;
  if (source.kind === 'static') {
    const text = resolveLocalized(source.value, locale).trim();
    return text || null;
  }
  if (source.kind === 'binding' && source.ref?.kind === 'itemField' && source.ref.field) {
    const v = resolveItemField(item, source.ref.field);
    if (v == null || v === '') return null;
    return String(v);
  }
  return null;
}

interface OverviewSlot {
  id: string;
  label: string | null;
  value: string;
}

function resolveOverview(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): {
  recipeId: string | null;
  columnSplit: 'auto' | 'stack' | 'split';
  slots: OverviewSlot[];
  heroUrl: string | null;
} {
  const doc = manifest.templateDocuments?.[item.templateId] as
    | {
        elements?: Array<{
          id?: string;
          overview?: {
            recipeId?: string;
            columnSplit?: 'auto' | 'stack' | 'split';
            hero?: { source?: { kind?: string; preferredIndex?: number } };
            slots?: Record<
              string,
              {
                source?: {
                  kind?: string;
                  value?: unknown;
                  ref?: { kind?: string; field?: string };
                };
                label?: unknown;
                emptyPolicy?: string;
              }
            >;
          };
        }>;
      }
    | undefined;

  const element =
    doc?.elements?.find((e) => e.id === item.templateElementId) ??
    doc?.elements?.[0];
  const overview = element?.overview;
  const heroUrl =
    mediaUrl(manifest, item.imageIds[0] ?? item.thumbId) ??
    mediaUrl(manifest, item.thumbId);
  const columnSplit = overview?.columnSplit ?? 'auto';

  if (!overview?.slots) {
    return { recipeId: null, columnSplit, slots: [], heroUrl };
  }

  const slots: OverviewSlot[] = [];
  for (const [id, assignment] of Object.entries(overview.slots)) {
    const value = resolveSlotValue(assignment.source ?? {}, item, manifest.locale);
    if (!value) {
      if (assignment.emptyPolicy === 'hide' || !assignment.emptyPolicy) continue;
    }
    slots.push({
      id,
      label: assignment.label
        ? resolveLocalized(assignment.label, manifest.locale) || null
        : null,
      value: value ?? '—',
    });
  }
  return {
    recipeId: overview.recipeId ?? null,
    columnSplit,
    slots,
    heroUrl,
  };
}

export interface ItemViewerProps {
  manifest: PublicShareManifest;
  item: SnapshotItem;
  onClose: () => void;
  pageUrl?: string;
}

export function ItemViewer({
  manifest,
  item,
  onClose,
  pageUrl,
}: ItemViewerProps) {
  const [tab, setTab] = useState<Tab>('overview');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const overview = useMemo(
    () => resolveOverview(manifest, item),
    [manifest, item],
  );

  const useSplit = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const wide = window.innerWidth >= 600;
    if (overview.columnSplit === 'stack') return false;
    if (overview.columnSplit === 'split') return wide;
    return wide && Boolean(overview.heroUrl);
  }, [overview.columnSplit, overview.heroUrl]);

  const photos = useMemo(() => {
    const out: Array<{
      thumbUrl: string;
      fullUrl: string;
      label: string;
    }> = [];
    for (const id of item.imageIds) {
      const fullUrl = fullUrlForMedia(manifest, id);
      if (!fullUrl) continue;
      out.push({
        thumbUrl: thumbUrlForMedia(manifest, id),
        fullUrl,
        label: item.title,
      });
    }
    if (out.length === 0 && item.thumbId) {
      const fullUrl = fullUrlForMedia(manifest, item.thumbId);
      if (fullUrl) {
        out.push({
          thumbUrl: thumbUrlForMedia(manifest, item.thumbId),
          fullUrl,
          label: item.title,
        });
      }
    }
    return out;
  }, [manifest, item]);

  const categories = item.categoryIds
    .map((id) => manifest.categories.find((c) => c.id === id)?.name)
    .filter(Boolean);
  const tags = item.tagIds
    .map((id) => manifest.tags.find((t) => t.id === id)?.name)
    .filter(Boolean);

  const pinPageUrl =
    pageUrl ||
    (typeof window !== 'undefined'
      ? window.location.href.split('#')[0]
      : '');

  const absMedia = (url: string) =>
    typeof window === 'undefined' ? url : absolutizeUrl(url, window.location.origin);

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={useSplit ? styles.panelWide : styles.panel}
        data-theme={manifest.theme}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div>
            <h2>{item.title}</h2>
            {overview.recipeId ? (
              <p className={styles.recipeHint}>Overview · {overview.recipeId}</p>
            ) : (
              <p className={styles.recipeHint}>Overview</p>
            )}
          </div>
          <button type="button" className={styles.close} onClick={onClose}>
            Close
          </button>
        </header>

        <nav className={styles.tabs}>
          <button
            type="button"
            className={tab === 'overview' ? styles.tabActive : styles.tab}
            onClick={() => setTab('overview')}
          >
            Overview
          </button>
          <button
            type="button"
            className={tab === 'photos' ? styles.tabActive : styles.tab}
            onClick={() => setTab('photos')}
          >
            Photos
          </button>
        </nav>

        <div className={styles.body}>
          {tab === 'overview' ? (
            <div className={useSplit ? styles.overviewSplit : styles.overview}>
              {overview.heroUrl ? (
                <button
                  type="button"
                  className={styles.hero}
                  onClick={() => {
                    const idx = photos.findIndex(
                      (p) => p.fullUrl === overview.heroUrl,
                    );
                    setLightboxIndex(idx >= 0 ? idx : 0);
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={
                      item.imageIds[0]
                        ? thumbUrlForMedia(manifest, item.imageIds[0])
                        : overview.heroUrl
                    }
                    alt=""
                    onError={(e) => {
                      const el = e.currentTarget;
                      if (el.src.includes('_thumb')) {
                        el.src = el.src.replace(/_thumb(?=\?|$)/, '');
                      } else if (overview.heroUrl) {
                        el.src = overview.heroUrl;
                      }
                    }}
                  />
                </button>
              ) : (
                <div
                  className={styles.heroFallback}
                  style={{
                    backgroundColor: item.geometry.backgroundColor ?? '#2d2d2d',
                  }}
                />
              )}

              <div className={styles.overviewBody}>
                <h3>{item.title}</h3>
                {item.description ? <p>{item.description}</p> : null}

                {overview.slots.length > 0 ? (
                  <dl className={styles.slots}>
                    {overview.slots.map((s) => (
                      <div key={s.id} className={styles.slot}>
                        {s.label ? <dt>{s.label}</dt> : null}
                        <dd>{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <div className={styles.legacyMeta}>
                    {item.amount != null ? (
                      <p>
                        <strong>Qty</strong> {item.amount}
                      </p>
                    ) : null}
                    {item.propertyText ? (
                      <p>
                        <strong>Details</strong> {item.propertyText}
                      </p>
                    ) : null}
                  </div>
                )}

                {categories.length > 0 ? (
                  <p className={styles.chips}>
                    {categories.map((c) => (
                      <span key={c}>{c}</span>
                    ))}
                  </p>
                ) : null}
                {tags.length > 0 ? (
                  <p className={styles.chips}>
                    {tags.map((t) => (
                      <span key={t}>#{t}</span>
                    ))}
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className={styles.photosGallery}>
              {photos.length === 0 ? (
                <p className={styles.empty}>No photos for this item.</p>
              ) : (
                <div className={styles.photosGrid}>
                  {photos.map((p, i) => (
                    <div key={`${p.fullUrl}-${i}`} className={styles.photoCell}>
                      <button
                        type="button"
                        className={styles.photoThumb}
                        onClick={() => setLightboxIndex(i)}
                        aria-label={`Open photo ${i + 1}`}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={p.thumbUrl}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            const el = e.currentTarget;
                            if (el.src.includes('_thumb')) {
                              el.src = el.src.replace(/_thumb(?=\?|$)/, '');
                            }
                          }}
                        />
                      </button>
                      {manifest.visibility === 'public' && pinPageUrl ? (
                        <PinterestPinButton
                          pageUrl={pinPageUrl}
                          mediaUrl={absMedia(p.fullUrl)}
                          description={p.label || item.title}
                          className={styles.photoPin}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {lightboxIndex != null ? (
        <PhotoLightbox
          photos={photos.map((p) => ({
            fullUrl: resolveShareLightboxUrl({
              fullUrl: p.fullUrl,
              thumbUrl: p.thumbUrl,
              animateImages: item.animateImages,
            }),
            label: p.label,
          }))}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
          pageUrl={manifest.visibility === 'public' ? pinPageUrl : undefined}
        />
      ) : null}
    </div>
  );
}
